import React from 'react'
import { breakpointRawMinWidthValues } from '../Grid/Grid.constants'

const adjustMaxWidth = (breakpointMinWidth: number) => breakpointMinWidth - 0.02

export const mediaBreakpointsDown = {
  tabletSm: `(max-width: ${adjustMaxWidth(
    breakpointRawMinWidthValues.tabletSm
  )}px)`,
  tabletMd: `(max-width: ${adjustMaxWidth(
    breakpointRawMinWidthValues.tabletMd
  )}px)`,
  tabletLg: `(max-width: ${adjustMaxWidth(
    breakpointRawMinWidthValues.tabletLg
  )}px)`,
  desktopMd: `(max-width: ${adjustMaxWidth(
    breakpointRawMinWidthValues.desktopMd
  )}px)`,
}

/** Short viewports (high zoom): no room for sticky elements */
export const mediaHeightDown = {
  short: '(max-height: 479.98px)',
}

/** Very narrow viewports (400% zoom, small phones) */
export const mediaWidthDown = {
  mobileSm: '(max-width: 439.98px)',
}

export const getIsUsingSmallScreen = () =>
  globalThis.matchMedia?.(mediaBreakpointsDown.tabletMd).matches

/** Reactive getIsUsingSmallScreen */
export function useIsUsingSmallScreen() {
  const [isSmallScreen, setIsSmallScreen] = React.useState(
    () => getIsUsingSmallScreen() ?? false
  )

  React.useEffect(() => {
    const mediaQuery = globalThis.matchMedia?.(mediaBreakpointsDown.tabletMd)
    if (!mediaQuery) {
      return
    }

    const update = () => setIsSmallScreen(mediaQuery.matches)
    update()
    mediaQuery.addEventListener('change', update)

    return () => mediaQuery.removeEventListener('change', update)
  }, [])

  return isSmallScreen
}

export const wait = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms))

export function getBottomOffsetForFooterUseCase(
  footerEl: HTMLDivElement,
  globalBottomOffset: number
) {
  const viewportHeight = globalThis.innerHeight
  const footerRect = footerEl.getBoundingClientRect()
  const footerTop = footerRect.y ?? footerRect.top
  const footerHeight = footerRect.height
  const isSmallScreen = getIsUsingSmallScreen()
  const isPageFooterSticky = footerEl.dataset.sticky === 'sticky'

  if (isSmallScreen) {
    if (isPageFooterSticky) {
      return globalBottomOffset + viewportHeight - footerTop
    }

    return globalBottomOffset + footerHeight
  }

  if (isPageFooterSticky) {
    // align with the sticky footer in the rest of use cases
    return viewportHeight - footerRect.bottom
  }

  return null
}

export function getGlobalBottomOffset(containerEl: HTMLDivElement) {
  const lowerBoundary = containerEl.getBoundingClientRect()
  const lowerBoundaryTopOffset = lowerBoundary.bottom
  const viewportHeight = document.documentElement.clientHeight
  return Math.max(viewportHeight - lowerBoundaryTopOffset, 0)
}

export function getGlobalTopOffset(containerEl: HTMLDivElement) {
  const upperBoundary = containerEl.getBoundingClientRect()
  const upperBoundaryTopOffset = upperBoundary.y ?? upperBoundary.top
  return Math.max(upperBoundaryTopOffset, 0)
}

export function getRightOffset(containerEl: HTMLDivElement) {
  const containerRight = containerEl.getBoundingClientRect().right
  const viewportWidth = document.documentElement.clientWidth

  return viewportWidth - containerRight
}

export function getIsIntersectingVertically(
  upperEl: HTMLElement,
  lowerEl: HTMLElement
) {
  return (
    upperEl.getBoundingClientRect().bottom > lowerEl.getBoundingClientRect().top
  )
}
